import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileDown, User, Hospital, FileText, Loader2, Download,
  Eye, EyeOff, Filter, Plus, Minus, CalendarDays,
  Mail, Send, X, BookUser, Trash2, MapPin, Truck, Shield
} from 'lucide-react';
import { usePdfTemplates } from '@/hooks/usePdfTemplates';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import DateInput from '@/components/ui/date-input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import PatientPicker from '@/components/shared/PatientPicker';

const api = axios.create({ baseURL: '/api' });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const PROFILE_LABELS = {
  low: 'Bajo', moderate: 'Moderado', moderate_plus: 'Moderado Plus',
  high: 'Alto', ultra_high: 'Ultra Alto', extra_high: 'Extra Alto',
};

export default function ImplantExportDialog({
  open,
  onOpenChange,
  implants = [],
  top3Primary = [],
  top3Alt = [],
  queryParams = {},
  patientId,
  patientName = '',
  allowPatientSelection = false,
}) {
  const { admin, isAdmin } = useAuth();
  const navigate = useNavigate();
  const canSendEmails = isAdmin || admin?.can_send_emails === true;
  const [selectedRefs, setSelectedRefs] = useState(new Set());
  const [quantities, setQuantities] = useState({});
  const [brandFilter, setBrandFilter] = useState('');
  const [template, setTemplate] = useState('v1_private');
  const { templates: pdfTemplatesList } = usePdfTemplates();
  const [anonymous, setAnonymous] = useState(false);
  const [surgeryDate, setSurgeryDate] = useState('');
  const [applyToNote, setApplyToNote] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Inline patient picker (used when allowPatientSelection=true)
  const [pickedPatient, setPickedPatient] = useState(null);

  // Reset patient when dialog reopens
  useEffect(() => {
    if (!open) {
      setPickedPatient(null);
    }
  }, [open]);

  const effectivePatientId = pickedPatient?.id || patientId;
  const effectivePatientName = pickedPatient?.name || patientName;

  // Location state
  const [surgeryLocation, setSurgeryLocation] = useState('');
  const [deliveryLocation, setDeliveryLocation] = useState('');
  const [insurance, setInsurance] = useState('');
  const [savedLocations, setSavedLocations] = useState([]);

  // Email state
  const [mode, setMode] = useState('download'); // 'download' | 'email'
  const [emailInput, setEmailInput] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [savedContacts, setSavedContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [showContacts, setShowContacts] = useState(false);

  // Load saved contacts and locations
  useEffect(() => {
    if (open) {
      loadSavedLocations();
      if (mode === 'email') {
        loadContacts();
      }
    }
  }, [open, mode]);

  // Pre-fill insurance from the patient's record (editable afterwards).
  // Falls back to "Pago Personal" if the patient has no saved insurance.
  useEffect(() => {
    if (!open || !effectivePatientId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/patients/${effectivePatientId}`);
        if (cancelled) return;
        const fromPatient = (res.data?.insurance_name || '').trim();
        setInsurance(fromPatient || 'Pago Personal');
      } catch {
        if (!cancelled) setInsurance('Pago Personal');
      }
    })();
    return () => { cancelled = true; };
  }, [open, effectivePatientId]);

  const loadSavedLocations = async () => {
    try {
      const res = await api.get('/implant-calculator/saved-locations');
      setSavedLocations(res.data);
    } catch { /* ignore */ }
  };

  const saveLocation = async (name, type) => {
    if (!name.trim()) return;
    try {
      const res = await api.post('/implant-calculator/saved-locations', { name: name.trim(), type });
      setSavedLocations(prev => [...prev, res.data]);
      toast.success('Ubicación guardada');
    } catch { toast.error('Error al guardar ubicación'); }
  };

  const deleteSavedLocation = async (locId) => {
    try {
      await api.delete(`/implant-calculator/saved-locations/${locId}`);
      setSavedLocations(prev => prev.filter(l => l.id !== locId));
    } catch { toast.error('Error al eliminar'); }
  };

  const loadContacts = async () => {
    setLoadingContacts(true);
    try {
      const res = await api.get('/implant-calculator/email-contacts');
      setSavedContacts(res.data);
    } catch {
      // Ignore
    } finally {
      setLoadingContacts(false);
    }
  };

  // Deduplicate and limit to 20.
  // Normaliza `reference` (algunos flujos — carrito precargado desde
  // "Modificar/Duplicar pedido" — traen solo `ref`). Sin esto, la dedup
  // y la selección por `imp.reference` descartaban esos items del PDF.
  const allImplants = useMemo(() => {
    const combined = [...top3Primary, ...implants, ...top3Alt].map(imp => ({
      ...imp,
      reference: imp.reference || imp.ref,
    }));
    const seen = new Set();
    return combined.filter(imp => {
      if (!imp.reference) return true;
      if (seen.has(imp.reference)) return false;
      seen.add(imp.reference);
      return true;
    });
  }, [implants, top3Primary, top3Alt]);

  const brands = useMemo(() => {
    const b = new Set();
    allImplants.forEach(imp => b.add(imp.brand));
    return Array.from(b).sort();
  }, [allImplants]);

  const effectiveBrandFilter = brandFilter || (brands.length === 1 ? brands[0] : '');

  const filteredImplants = useMemo(() => {
    if (!effectiveBrandFilter) return allImplants;
    return allImplants.filter(imp => imp.brand === effectiveBrandFilter);
  }, [allImplants, effectiveBrandFilter]);

  const toggleRef = (ref) => {
    setSelectedRefs(prev => {
      const next = new Set(prev);
      if (next.has(ref)) {
        next.delete(ref);
      } else {
        next.add(ref);
        if (!quantities[ref]) setQuantities(q => ({ ...q, [ref]: 2 }));
      }
      return next;
    });
  };

  const setQty = (ref, qty) => {
    setQuantities(q => ({ ...q, [ref]: Math.max(1, Math.min(3, qty)) }));
  };

  const selectAll = () => {
    const newSel = new Set(filteredImplants.map(i => i.reference));
    setSelectedRefs(newSel);
    const newQty = { ...quantities };
    filteredImplants.forEach(i => { if (!newQty[i.reference]) newQty[i.reference] = 2; });
    setQuantities(newQty);
  };

  const deselectAll = () => setSelectedRefs(new Set());

  const selectedBrands = useMemo(() => {
    const b = new Set();
    filteredImplants.forEach(imp => {
      if (selectedRefs.has(imp.reference)) b.add(imp.brand);
    });
    return b;
  }, [selectedRefs, filteredImplants]);

  const hasMixedBrands = selectedBrands.size > 1;

  const totalUnits = useMemo(() => {
    let total = 0;
    filteredImplants.forEach(imp => {
      if (selectedRefs.has(imp.reference)) total += (quantities[imp.reference] || 2);
    });
    return total;
  }, [selectedRefs, quantities, filteredImplants]);

  const formatDateForPdf = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  };

  // Email helpers
  const addEmail = (email) => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error('Email no válido');
      return;
    }
    if (recipients.includes(trimmed)) {
      toast.error('Email ya añadido');
      return;
    }
    setRecipients(prev => [...prev, trimmed]);
    setEmailInput('');
  };

  const removeEmail = (email) => {
    setRecipients(prev => prev.filter(e => e !== email));
  };

  const addFromContact = (contact) => {
    if (!recipients.includes(contact.email)) {
      setRecipients(prev => [...prev, contact.email]);
    }
    setShowContacts(false);
  };

  const deleteContact = async (contactId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/implant-calculator/email-contacts/${contactId}`);
      setSavedContacts(prev => prev.filter(c => c.id !== contactId));
      toast.success('Contacto eliminado');
    } catch {
      toast.error('Error al eliminar contacto');
    }
  };

  const getPayload = () => {
    const selected = filteredImplants
      .filter(imp => selectedRefs.has(imp.reference))
      .map(imp => ({ ...imp, qty: quantities[imp.reference] || 2 }));
    return {
      patient_id: effectivePatientId,
      selected_implants: selected,
      template,
      anonymous,
      query_params: queryParams,
      surgery_date: formatDateForPdf(surgeryDate),
      surgery_location: surgeryLocation || null,
      delivery_location: deliveryLocation || null,
      insurance: insurance || null,
      apply_to_surgical_note: applyToNote,
    };
  };

  const handleDownload = async () => {
    if (allowPatientSelection && !effectivePatientId) return toast.error('Selecciona un paciente');
    if (selectedRefs.size === 0) return toast.error('Selecciona al menos una prótesis');
    if (hasMixedBrands) return toast.error('No se pueden mezclar marcas');
    if (!surgeryDate) return toast.error('Selecciona la fecha de cirugía');

    setGenerating(true);
    try {
      const response = await api.post('/implant-calculator/export-pdf', getPayload(), {
        responseType: 'blob', timeout: 30000,
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const label = anonymous ? 'Anonimo' : (effectivePatientName || 'paciente').replace(/ /g, '_');
      a.download = `Pedido_Protesis_${label}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      // Parse summary headers (sent by backend) for a richer post-action toast
      let summary = null;
      try {
        const raw = response.headers?.['x-implant-summary'] || response.headers?.get?.('x-implant-summary');
        if (raw) summary = JSON.parse(raw);
      } catch (_e) { /* ignore */ }

      const dateStr = new Date().toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const lines = [];
      const savedInDb = !!summary?.file_id;
      if (savedInDb) {
        lines.push(`PDF guardado en archivos del paciente · ${dateStr}`);
      } else {
        lines.push(`PDF descargado · ${dateStr}`);
      }
      if (summary?.applied_to_note) {
        const r = summary.implants_added_right || 0;
        const l = summary.implants_added_left || 0;
        const noteWord = summary.created_new_note ? 'nota quirúrgica creada' : 'nota quirúrgica';
        lines.push(`Aplicado a ${noteWord} · ${r} derecha / ${l} izquierda`);
      }

      if (!savedInDb) {
        // El backend no pudo guardar el pedido en la ficha del paciente.
        // Avisamos y NO borramos el pedido antiguo para no perder datos.
        toast.error(
          'El PDF se descargó pero no se pudo guardar en el historial del paciente. Reintentá o contactá soporte.',
          { duration: 10000 },
        );
      } else {
        toast.success(lines.join(' · '), {
          duration: 8000,
          action: summary?.note_id && effectivePatientId
            ? {
                label: 'Ver nota',
                onClick: () =>
                  navigate(`/admin/clinical-history?patient=${effectivePatientId}&tab=surgical`),
              }
            : undefined,
        });
      }

      // Reemplazo del pedido antiguo — SOLO si el nuevo pedido quedó guardado
      // en la base (file_id presente) y el paciente coincide. El pedido
      // antiguo NO se borra: se archiva como "reemplazado" (recuperable).
      try {
        const raw = sessionStorage.getItem('prosthesis_replace_order');
        if (raw && savedInDb) {
          const { order_id, patient_id } = JSON.parse(raw);
          if (order_id && (!patient_id || patient_id === effectivePatientId)) {
            sessionStorage.removeItem('prosthesis_replace_order');
            await api.post(`/prosthesis-orders/${order_id}/archive-replaced`, { replaced_by: summary?.file_id || null });
            toast.success('Pedido anterior archivado como reemplazado (recuperable desde la pestaña de pedidos)');
          }
        }
      } catch { /* no-op: si falla, el pedido antiguo queda como respaldo */ }

      onOpenChange(false);
    } catch {
      toast.error('Error al generar el PDF');
    } finally {
      setGenerating(false);
    }
  };

  const handleSendEmail = async () => {
    if (allowPatientSelection && !effectivePatientId) return toast.error('Selecciona un paciente');
    if (selectedRefs.size === 0) return toast.error('Selecciona al menos una prótesis');
    if (hasMixedBrands) return toast.error('No se pueden mezclar marcas');
    if (!surgeryDate) return toast.error('Selecciona la fecha de cirugía');
    if (recipients.length === 0) return toast.error('Añade al menos un destinatario');

    setGenerating(true);
    try {
      const payload = {
        ...getPayload(),
        recipients,
        save_contacts: true,
        email_subject: emailSubject || undefined,
        email_body: emailBody || undefined,
      };
      const res = await api.post('/implant-calculator/send-email', payload);
      toast.success(res.data.message);
      loadContacts(); // Refresh contacts list

      // Reemplazo del pedido antiguo si venimos del flujo "Modificar modelos"
      // y el paciente coincide. Archivado suave — nunca se borra.
      try {
        const raw = sessionStorage.getItem('prosthesis_replace_order');
        if (raw) {
          const { order_id, patient_id } = JSON.parse(raw);
          if (order_id && (!patient_id || patient_id === effectivePatientId)) {
            sessionStorage.removeItem('prosthesis_replace_order');
            await api.post(`/prosthesis-orders/${order_id}/archive-replaced`, { replaced_by: res.data?.file_id || null });
            toast.success('Pedido anterior archivado como reemplazado (recuperable desde la pestaña de pedidos)');
          }
        }
      } catch { /* no-op */ }

      onOpenChange(false);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al enviar email';
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const canGenerate = selectedRefs.size > 0 && !hasMixedBrands && surgeryDate;
  const canSendEmail = canGenerate && recipients.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="implant-export-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileDown size={20} className="text-[#0E3463]" />
            Exportar Recomendaciones
          </DialogTitle>
          <DialogDescription>
            Descarga el PDF o envíalo directamente por email
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 pr-1">
          {/* Inline patient picker (only when allowPatientSelection=true) */}
          {allowPatientSelection && (
            <div className="space-y-1.5 border border-indigo-100 bg-brand-soft/40/40 rounded-lg p-3">
              <Label className="text-xs font-semibold flex items-center gap-1 text-[#0E3463]">
                <User size={12} /> Paciente *
              </Label>
              <PatientPicker
                value={pickedPatient}
                onChange={setPickedPatient}
                autoFocus
                testId="export-patient"
              />
            </div>
          )}

          {/* Mode toggle */}
          <div className="flex gap-1 p-0.5 bg-gray-100 rounded-lg w-fit">
            <button
              type="button"
              onClick={() => setMode('download')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-all ${
                mode === 'download' ? 'bg-white shadow text-[#0E3463] font-semibold' : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="mode-download"
            >
              <Download size={13} /> Descargar PDF
            </button>
            <button
              type="button"
              onClick={() => setMode('email')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-all ${
                mode === 'email' ? 'bg-white shadow text-[#0E3463] font-semibold' : 'text-gray-500 hover:text-gray-700'
              } ${!canSendEmails ? 'hidden' : ''}`}
              data-testid="mode-email"
            >
              <Mail size={13} /> Enviar por Email
            </button>
          </div>

          {/* Brand filter */}
          {brands.length > 1 && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold flex items-center gap-1">
                <Filter size={12} /> Filtrar por marca
              </Label>
              <div className="flex gap-2">
                {brands.map(b => (
                  <button
                    key={b}
                    type="button"
                    onClick={() => { setBrandFilter(prev => prev === b ? '' : b); setSelectedRefs(new Set()); }}
                    className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                      effectiveBrandFilter === b
                        ? 'bg-[#0E3463] text-white border-[#0E3463]'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-[#B87C94]'
                    }`}
                    data-testid={`brand-filter-${b}`}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Implant selection table */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold">Prótesis ({filteredImplants.length})</Label>
              <div className="flex gap-2">
                <button type="button" onClick={selectAll} className="text-[10px] text-[#0E3463] hover:underline" data-testid="select-all-btn">Todas</button>
                <button type="button" onClick={deselectAll} className="text-[10px] text-gray-500 hover:underline" data-testid="deselect-all-btn">Ninguna</button>
              </div>
            </div>
            <div className="border rounded-lg overflow-hidden max-h-[200px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-[#B87C94] border-b sticky top-0 z-10">
                  <tr className="text-white">
                    <th className="px-2 py-1.5 text-center w-7"></th>
                    <th className="px-1 py-1.5 text-center font-medium w-14">Uds.</th>
                    <th className="px-2 py-1.5 text-left font-medium">Línea</th>
                    <th className="px-2 py-1.5 text-left font-medium">Ref</th>
                    <th className="px-2 py-1.5 text-center font-medium">Vol</th>
                    <th className="px-2 py-1.5 text-center font-medium">Base</th>
                    <th className="px-2 py-1.5 text-center font-medium">Proy</th>
                    <th className="px-2 py-1.5 text-left font-medium">Perfil</th>
                    <th className="px-2 py-1.5 text-center font-medium">Tipo</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredImplants.map((imp) => {
                    const isSelected = selectedRefs.has(imp.reference);
                    const qty = quantities[imp.reference] || 2;
                    return (
                      <tr key={imp.reference} className={`border-b cursor-pointer transition-colors ${isSelected ? 'bg-[#F7EAF0]/40' : 'hover:bg-gray-50'}`}
                        onClick={() => toggleRef(imp.reference)} data-testid={`implant-row-${imp.reference}`}>
                        <td className="px-2 py-1.5 text-center">
                          <Checkbox checked={isSelected} onCheckedChange={() => toggleRef(imp.reference)} className="pointer-events-none" />
                        </td>
                        <td className="px-1 py-1 text-center" onClick={(e) => e.stopPropagation()}>
                          {isSelected ? (
                            <div className="flex items-center justify-center gap-0.5">
                              <button type="button" onClick={() => setQty(imp.reference, qty - 1)}
                                className="w-5 h-5 rounded bg-[#0E3463] text-white flex items-center justify-center hover:bg-[#0E3463]/80 text-[10px]"
                                data-testid={`qty-minus-${imp.reference}`}><Minus size={10} /></button>
                              <span className="w-5 text-center font-bold text-[11px]" data-testid={`qty-value-${imp.reference}`}>{qty}</span>
                              <button type="button" onClick={() => setQty(imp.reference, qty + 1)}
                                className="w-5 h-5 rounded bg-[#0E3463] text-white flex items-center justify-center hover:bg-[#0E3463]/80 text-[10px]"
                                data-testid={`qty-plus-${imp.reference}`}><Plus size={10} /></button>
                            </div>
                          ) : <span className="text-gray-400 text-[10px]">—</span>}
                        </td>
                        <td className="px-2 py-1.5 text-gray-600 text-[11px]">{imp.product_line}</td>
                        <td className="px-2 py-1.5 font-mono text-[10px] font-semibold">{imp.reference}</td>
                        <td className="px-2 py-1.5 text-center font-semibold">{imp.volume_cc}cc</td>
                        <td className="px-2 py-1.5 text-center">{imp.base_width_mm}mm</td>
                        <td className="px-2 py-1.5 text-center">{imp.projection_mm}mm</td>
                        <td className="px-2 py-1.5">
                          <Badge variant="outline" className="text-[9px] px-1">{PROFILE_LABELS[imp.profile] || imp.profile}</Badge>
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <Badge variant="outline" className="text-[9px]">{imp.type === 'round' ? 'Red' : 'Anat'}</Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {selectedRefs.size > 0 && (
              <p className="text-[10px] text-[#0E3463] font-medium">
                {selectedRefs.size} modelo{selectedRefs.size !== 1 ? 's' : ''} &middot; {totalUnits} ud{totalUnits !== 1 ? 's' : ''}.
                {hasMixedBrands && <span className="text-red-500 ml-2">No se pueden mezclar marcas</span>}
              </p>
            )}
          </div>

          {/* Options: date + locations + template + anonymous */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold flex items-center gap-1">
                <CalendarDays size={12} /> Fecha Cirugía
              </Label>
              <DateInput value={surgeryDate} onChange={(iso) => setSurgeryDate(iso)}
                className="text-xs h-8" data-testid="surgery-date-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold flex items-center gap-1">
                <MapPin size={12} /> Lugar Cirugía
              </Label>
              <div className="flex gap-1">
                <Input value={surgeryLocation} onChange={(e) => setSurgeryLocation(e.target.value)}
                  placeholder="Ej: Hospital La Fe" className="text-xs h-8 flex-1" data-testid="surgery-location-input" />
                {surgeryLocation.trim() && !savedLocations.some(l => l.type === 'surgery' && l.name === surgeryLocation.trim()) && (
                  <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => saveLocation(surgeryLocation, 'surgery')} title="Guardar"><Plus size={12} /></Button>
                )}
              </div>
              {savedLocations.filter(l => l.type === 'surgery').length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {savedLocations.filter(l => l.type === 'surgery').map(loc => (
                    <button key={loc.id} type="button" onClick={() => setSurgeryLocation(loc.name)}
                      className={`text-[9px] px-1.5 py-0.5 rounded-full border transition-all ${surgeryLocation === loc.name ? 'bg-brand-soft/60 border-brand-medium text-brand-navy' : 'bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-600'}`}>
                      {loc.name}
                      <span className="ml-1 opacity-50 hover:opacity-100 cursor-pointer" onClick={(e) => { e.stopPropagation(); deleteSavedLocation(loc.id); }}>&times;</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold flex items-center gap-1">
                <Truck size={12} /> Lugar Entrega
              </Label>
              <div className="flex gap-1">
                <Input value={deliveryLocation} onChange={(e) => setDeliveryLocation(e.target.value)}
                  placeholder="Ej: Clínica Adrianzén" className="text-xs h-8 flex-1" data-testid="delivery-location-input" />
                {deliveryLocation.trim() && !savedLocations.some(l => l.type === 'delivery' && l.name === deliveryLocation.trim()) && (
                  <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => saveLocation(deliveryLocation, 'delivery')} title="Guardar"><Plus size={12} /></Button>
                )}
              </div>
              {savedLocations.filter(l => l.type === 'delivery').length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {savedLocations.filter(l => l.type === 'delivery').map(loc => (
                    <button key={loc.id} type="button" onClick={() => setDeliveryLocation(loc.name)}
                      className={`text-[9px] px-1.5 py-0.5 rounded-full border transition-all ${deliveryLocation === loc.name ? 'bg-brand-soft/60 border-brand-medium text-brand-navy' : 'bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-600'}`}>
                      {loc.name}
                      <span className="ml-1 opacity-50 hover:opacity-100 cursor-pointer" onClick={(e) => { e.stopPropagation(); deleteSavedLocation(loc.id); }}>&times;</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold flex items-center gap-1">
                <Shield size={12} /> Aseguradora
                <span className="text-[10px] text-gray-400 font-normal">(editable)</span>
                {insurance.trim().toLowerCase() === 'pago personal' && (
                  <span
                    className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-100 border border-red-300 text-red-700 text-[10px] font-semibold"
                    data-testid="insurance-personal-flag"
                    title="Cirugía sin cobertura aseguradora — preparar presupuesto manual"
                  >
                    <span className="text-red-600">*</span> Presupuesto manual
                  </span>
                )}
              </Label>
              <div className="flex gap-1">
                <Input
                  value={insurance}
                  onChange={(e) => setInsurance(e.target.value)}
                  placeholder="Aseguradora o Pago Personal"
                  className={`text-xs h-8 flex-1 ${
                    insurance.trim().toLowerCase() === 'pago personal'
                      ? 'border-red-300 focus-visible:ring-red-300 bg-red-50/40'
                      : ''
                  }`}
                  data-testid="insurance-input"
                />
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {['Pago Personal', 'Asisa', 'Adeslas', 'Sanitas', 'DKV', 'Mapfre'].map(opt => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setInsurance(opt)}
                    className={`text-[9px] px-1.5 py-0.5 rounded-full border transition-all ${
                      insurance === opt
                        ? 'bg-brand-soft/60 border-brand-medium text-brand-navy'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-600'
                    }`}
                    data-testid={`insurance-quick-${opt.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Encabezado</Label>
              <div className="grid grid-cols-3 gap-1">
                {pdfTemplatesList.map(t => {
                  const Icon = t.icon || FileText;
                  return (
                  <button key={t.id} type="button" onClick={() => setTemplate(t.id)}
                    className={`p-1.5 rounded-lg border-2 text-center transition-all ${
                      template === t.id ? 'border-indigo-500 bg-brand-soft/40' : 'border-gray-200 hover:border-gray-300'
                    }`} data-testid={`template-${t.id}`}>
                    <Icon size={13} className={`mx-auto mb-0.5 ${template === t.id ? 'text-brand-navy' : 'text-gray-400'}`} />
                    <p className="text-[9px] font-medium">{t.name}</p>
                  </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Paciente</Label>
              <div className="grid grid-cols-2 gap-1">
                <button type="button" onClick={() => setAnonymous(false)}
                  className={`p-1.5 rounded-lg border-2 text-center transition-all ${!anonymous ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'}`}
                  data-testid="patient-full-name">
                  <Eye size={13} className={`mx-auto mb-0.5 ${!anonymous ? 'text-emerald-600' : 'text-gray-400'}`} />
                  <p className="text-[9px] font-medium">Completo</p>
                </button>
                <button type="button" onClick={() => setAnonymous(true)}
                  className={`p-1.5 rounded-lg border-2 text-center transition-all ${anonymous ? 'border-amber-500 bg-brand-soft/40' : 'border-gray-200 hover:border-gray-300'}`}
                  data-testid="patient-anonymous">
                  <EyeOff size={13} className={`mx-auto mb-0.5 ${anonymous ? 'text-brand-deep' : 'text-gray-400'}`} />
                  <p className="text-[9px] font-medium">Iniciales</p>
                </button>
              </div>
            </div>
          </div>

          {/* Apply also to surgical clinical note */}
          <label
            className="flex items-start gap-2 px-3 py-2 rounded-lg border border-brand-medium bg-brand-soft/40/50 cursor-pointer hover:bg-brand-soft/40 transition-colors"
            data-testid="apply-to-note-toggle"
          >
            <input
              type="checkbox"
              checked={applyToNote}
              onChange={(e) => setApplyToNote(e.target.checked)}
              className="mt-0.5"
            />
            <div className="text-xs leading-snug">
              <p className="font-semibold text-[#0E3463]">
                Añadir también a la nota quirúrgica del paciente
              </p>
              <p className="text-gray-600 mt-0.5">
                Las prótesis se añaden como{' '}
                <code className="text-[10px]">implants_left/right</code> en la última nota
                quirúrgica no firmada (o se crea borrador).
              </p>
            </div>
          </label>

          {/* Email section */}
          {mode === 'email' && (
            <div className="space-y-3 border-t pt-3">
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold flex items-center gap-1">
                    <Mail size={12} /> Destinatarios
                  </Label>
                  {savedContacts.length > 0 && (
                    <button type="button" onClick={() => setShowContacts(!showContacts)}
                      className="text-[10px] text-[#0E3463] hover:underline flex items-center gap-1"
                      data-testid="toggle-contacts-btn">
                      <BookUser size={11} />
                      {showContacts ? 'Ocultar' : 'Contactos guardados'} ({savedContacts.length})
                    </button>
                  )}
                </div>

                {/* Saved contacts dropdown */}
                {showContacts && savedContacts.length > 0 && (
                  <div className="border rounded-lg bg-gray-50 p-2 space-y-1 max-h-[100px] overflow-y-auto" data-testid="saved-contacts-list">
                    {savedContacts.map(c => (
                      <div key={c.id} className="flex items-center justify-between px-2 py-1 rounded hover:bg-white cursor-pointer group transition-colors"
                        onClick={() => addFromContact(c)}>
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-medium text-gray-800 truncate">
                            {c.email}
                            {c.name && <span className="text-gray-500 ml-1">({c.name})</span>}
                          </p>
                          {c.company && <p className="text-[9px] text-[#B87C94]">{c.company}</p>}
                        </div>
                        <div className="flex items-center gap-1">
                          {recipients.includes(c.email) ? (
                            <Badge className="text-[8px] bg-emerald-100 text-emerald-700">Añadido</Badge>
                          ) : (
                            <Plus size={12} className="text-gray-400 group-hover:text-[#0E3463]" />
                          )}
                          <button type="button" onClick={(e) => deleteContact(c.id, e)}
                            className="p-0.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"
                            data-testid={`delete-contact-${c.id}`}>
                            <Trash2 size={10} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Email input */}
                <div className="flex gap-1.5">
                  <Input
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(emailInput); } }}
                    placeholder="correo@ejemplo.com"
                    className="text-xs h-8 flex-1"
                    data-testid="email-input"
                  />
                  <Button type="button" size="sm" variant="outline" onClick={() => addEmail(emailInput)}
                    className="h-8 px-2 text-xs" data-testid="add-email-btn">
                    <Plus size={12} />
                  </Button>
                </div>

                {/* Recipients list */}
                {recipients.length > 0 && (
                  <div className="flex flex-wrap gap-1.5" data-testid="recipients-list">
                    {recipients.map(email => (
                      <span key={email} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#0E3463]/10 text-[#0E3463] rounded-full text-[10px] font-medium">
                        {email}
                        <button type="button" onClick={() => removeEmail(email)} className="hover:text-red-500">
                          <X size={10} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-[9px] text-gray-500">
                  Los correos se guardarán automáticamente para futuras solicitudes
                </p>
              </div>

              {/* Optional subject & body */}
              <div className="grid grid-cols-1 gap-2">
                <div>
                  <Label className="text-[10px] text-gray-500">Asunto (opcional)</Label>
                  <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Solicitud de prótesis Mentor - 3 modelos"
                    className="text-xs h-8" data-testid="email-subject-input" />
                </div>
                <div>
                  <Label className="text-[10px] text-gray-500">Mensaje adicional (opcional)</Label>
                  <Textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)}
                    placeholder="Notas adicionales para el representante..."
                    rows={2} className="text-xs resize-none" data-testid="email-body-input" />
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="pt-3 border-t flex gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="export-cancel-btn">
            Cancelar
          </Button>
          {mode === 'download' ? (
            <Button onClick={handleDownload} disabled={generating || !canGenerate}
              className="bg-[#0E3463] hover:bg-[#0E3463]/90" data-testid="export-generate-btn">
              {generating ? <><Loader2 size={14} className="mr-2 animate-spin" />Generando...</>
                : <><Download size={14} className="mr-2" />Descargar PDF ({selectedRefs.size} mod., {totalUnits} uds.)</>}
            </Button>
          ) : (
            <Button onClick={handleSendEmail} disabled={generating || !canSendEmail}
              className="bg-[#B87C94] hover:bg-[#B87C94]/90" data-testid="export-send-email-btn">
              {generating ? <><Loader2 size={14} className="mr-2 animate-spin" />Enviando...</>
                : <><Send size={14} className="mr-2" />Enviar a {recipients.length} destinatario{recipients.length !== 1 ? 's' : ''}</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
